// eslint-disable-next-line import/no-cycle
import beneficiaryService from '@client/services/beneficiary';
import {Beneficiary, BeneficiaryType, TrustedContact} from '@simpleinvest/ms-custodian-sdk';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import {getUser} from '@simpleinvest/front-end-auth';
// eslint-disable-next-line import/no-cycle
import accountRequestService from '@client/services/accountRequest';
import cloneDeep from 'lodash.clonedeep';
import accountType from '@client/enums/accountType';
import {Constants} from '@client/util/constants';
import relationships from '@client/constants/clientRelationships';

const logger = new Logger({
  category: Category.Accounts,
  fileName: 'requests.js',
});

interface AccountsTrustedContact {
  accountRequestId: number;
  trustedContact: TrustedContact;
}
interface RelationshipDropdown {
  relationship: string;
  relationshipCode: string;
  relationshipId: number;
}

interface UpdateSharePercentOfBeneficiaries {
  accountRequestId: number;
  beneficiaries: Beneficiary[];
  beneficiaryType: BeneficiaryType;
}

interface BeneficiariesByAccountRequestId {
  accountRequestId: number;
  beneficiaries: Beneficiary[];
}

interface BeneficiaryByAccountRequestId {
  accountRequestId: number;
  beneficiary: Beneficiary;
}
interface HasBeneficiaryCheck {
  accountRequestId: number;
  beneficiaries: boolean;
}

interface State {
  existingBeneficiaries: Beneficiary[];
  beneficiariesByAccountRequestId: Record<number, Beneficiary[]>;
  trustedContacts: AccountsTrustedContact[];
  relationshipDropdown: RelationshipDropdown[];
  selectedIndividualAccount: boolean;
  isTrustedContactUpdated: boolean;
  isBeneficiaryUpdated: boolean;
  selectedToAddBeneficiary: boolean;
  selectedToAddTrustedContact: boolean;
  hasBeneficiaryCheck: HasBeneficiaryCheck[];
}

const relationsDropdown = Object.keys(relationships).map((key) => relationships[key]);

const initialState: State = {
  existingBeneficiaries: [],
  beneficiariesByAccountRequestId: {},
  trustedContacts: [],
  relationshipDropdown: relationsDropdown,
  selectedIndividualAccount: false,
  isTrustedContactUpdated: false,
  isBeneficiaryUpdated: false,
  selectedToAddBeneficiary: false,
  selectedToAddTrustedContact: false,
  hasBeneficiaryCheck: [],
};

const getters = {
  selectedToAddBeneficiary: (state: State): boolean => state.selectedToAddBeneficiary,
  selectedToAddTrustedContact: (state: State): boolean => state.selectedToAddTrustedContact,
  selectedToAddDesignations: (state: State): boolean => state.selectedToAddBeneficiary || state.selectedToAddTrustedContact,
  showOnlyTrustedContact: (state: State): boolean => state.selectedToAddTrustedContact && !state.selectedToAddBeneficiary,
  showOnlyBeneficiary: (state: State): boolean => state.selectedToAddBeneficiary && !state.selectedToAddTrustedContact,
  existingBeneficiaries: (state: State): Beneficiary[] => state.existingBeneficiaries,
  beneficiariesByAccountRequestId: (state: State): Record<number, Beneficiary[]> => state.beneficiariesByAccountRequestId,
  getBeneficiariesByAccountRequestId:
    (state: State) =>
    (accountRequestId: number): Beneficiary[] =>
      Object.prototype.hasOwnProperty.call(state.beneficiariesByAccountRequestId, accountRequestId)
        ? state.beneficiariesByAccountRequestId[accountRequestId]
        : [],
  getPrimaryBeneficiariesByAccountRequestId:
    (state: State) =>
    (accountRequestId: number): Beneficiary[] => {
      if (Object.prototype.hasOwnProperty.call(state.beneficiariesByAccountRequestId, accountRequestId)) {
        const all = state.beneficiariesByAccountRequestId[accountRequestId];

        return all.filter((beneficiary) => beneficiary.beneficiaryType === BeneficiaryType.Primary.toUpperCase());
      }

      return [];
    },
  getContingentBeneficiariesByAccountRequestId:
    (state: State) =>
    (accountRequestId: number): Beneficiary[] => {
      if (Object.prototype.hasOwnProperty.call(state.beneficiariesByAccountRequestId, accountRequestId)) {
        const all = state.beneficiariesByAccountRequestId[accountRequestId];

        return all.filter((beneficiary) => beneficiary.beneficiaryType === BeneficiaryType.Contingent.toUpperCase());
      }

      return [];
    },
  trustedContacts: (state: State): AccountsTrustedContact[] => state.trustedContacts,
  relationshipDropdown: (state: State): RelationshipDropdown[] => {
    const dropdown = [];

    state.relationshipDropdown.forEach((relationshipItem) => {
      dropdown.push({relationship: relationshipItem.relationship, relationshipCode: relationshipItem.relationshipCode});
    });

    return dropdown;
  },
  findRelationshipText:
    (state: State) =>
    (relationshipCode: string): string => {
      const match = state.relationshipDropdown.find(
        (relationshipItem) => relationshipCode === relationshipItem.relationshipCode
      );

      return match.relationship || '';
    },
  selectedIndividualAccount: (state: State): boolean => state.selectedIndividualAccount,
  designationsUpdated: (state: State): boolean => state.isTrustedContactUpdated || state.isBeneficiaryUpdated,
  isTrustedContactUpdated: (state: State): boolean => state.isTrustedContactUpdated,
  isBeneficiaryUpdated: (state: State): boolean => state.isBeneficiaryUpdated,
  isSsnUniqueForAccountRequestId:
    (state: State) =>
    (accountRequestId: number, ssn: string): boolean => {
      const allBeneficiaries = Object.prototype.hasOwnProperty.call(state.beneficiariesByAccountRequestId, accountRequestId)
        ? state.beneficiariesByAccountRequestId[accountRequestId]
        : [];

      const hasMatchingSsn = allBeneficiaries.some(
        (matchingBeneficiary) => matchingBeneficiary.socialSecurityNumber === ssn || false
      );

      return !hasMatchingSsn;
    },
};

const actions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedToAddBeneficiary({commit}: any, payload: boolean): void {
    commit('SET_SELECTED_ADD_BENEFICIARY_OPTION', payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedToAddTrustedContact({commit}: any, payload: boolean): void {
    commit('SET_SELECTED_ADD_TRUSTED_CONTACT_OPTION', payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setAllBeneficiariesByAccountRequestId({commit}: any, payload: Record<number, Beneficiary[]>): void {
    commit('SET_ALL_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID', payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setBeneficiariesByAccountRequestId({commit}: any, payload: BeneficiariesByAccountRequestId): void {
    commit('SET_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID', payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addBeneficiaryToExistingByAccountRequestId({commit, getters: get}: any, payload: BeneficiaryByAccountRequestId): void {
    const {accountRequestId, beneficiary} = payload;

    const {getBeneficiariesByAccountRequestId} = get;
    const currentBeneficiaries = getBeneficiariesByAccountRequestId(accountRequestId);

    currentBeneficiaries.push(beneficiary);

    const newPayload = {accountRequestId, beneficiaries: currentBeneficiaries};

    commit('SET_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID', newPayload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateSharePercentOfBeneficiaries({commit, getters: get}: any, payload: UpdateSharePercentOfBeneficiaries): void {
    const {accountRequestId, beneficiaries, beneficiaryType} = payload;

    const {getContingentBeneficiariesByAccountRequestId, getPrimaryBeneficiariesByAccountRequestId} = get;

    const beneficiariesToAppend =
      beneficiaryType === BeneficiaryType.Primary
        ? getContingentBeneficiariesByAccountRequestId(accountRequestId)
        : getPrimaryBeneficiariesByAccountRequestId(accountRequestId);

    const newPayload = {accountRequestId, beneficiaries: [...beneficiariesToAppend, ...beneficiaries]};

    commit('SET_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID', newPayload);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async fetchBeneficiaries({commit, rootGetters}: any): Promise<void> {
    const clUserId = rootGetters['cognitoUser/userId'];

    try {
      const bennies = await beneficiaryService.getBeneficiariesForClUserId(clUserId);

      commit('SET_BENEFICIARIES', bennies);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'fetchBeneficiaries',
        message: `error fetching beneficiaries for clUserId: ${clUserId}`,
      });
      logger.logException({error});
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setAccountsTrustedContact({commit}: any, activeAccountRequests): void {
    commit('SET_TRUSTED_CONTACTS', activeAccountRequests);
    commit('SET_SELECTED_OPTION', activeAccountRequests);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUpdatedTrustedContact({commit}: any, trustedContact: AccountsTrustedContact): void {
    commit('SET_UPDATED_TRUSTED_CONTACT', trustedContact);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async removeTrustedContact({commit, getters: get}: any, accountRequestId: number): Promise<void> {
    try {
      const {trustedContact} = get.trustedContacts.find((account) => account.accountRequestId === accountRequestId);

      if (trustedContact.id) {
        await accountRequestService.removeTrustedContact(accountRequestId);
      }

      const payload = {
        accountRequestId,
        trustedContact: null,
      };

      commit('SET_UPDATED_TRUSTED_CONTACT', payload);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'removeTrustedContact',
        message: `error removing Trusted Contact for account requestId: ${accountRequestId}`,
      });
      logger.logException({error});
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getSelectedAccountDetails({commit, dispatch}: any, accountRequestId: number): Promise<void> {
    try {
      const accountDetails = await beneficiaryService.getSelectedAccountDetails(accountRequestId);

      commit('SET_SELECTED_ACCOUNT_DESIGNATIONS', accountDetails);
      dispatch('account/requests/setCurrentAccount', accountRequestId, {root: true});
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getSelectedAccountDetails',
        message: `error fetching account details for request id: ${accountRequestId}`,
      });
      logger.logException({error});
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async saveTrustedContact({commit, getters: get, rootGetters}: any): Promise<void> {
    const clUserId = rootGetters['cognitoUser/userId'];

    const accountsTrustedContact = get.trustedContacts.filter((request) => request.trustedContact);

    if (accountsTrustedContact.length > 0) {
      try {
        const response = await accountRequestService.saveTrustedContact({
          clUserId,
          accountsTrustedContact,
        });

        response.data.forEach((account) => {
          const {accountRequestId, trustedContact} = account;
          const payload = {
            accountRequestId,
            trustedContact,
          };

          commit('SET_UPDATED_TRUSTED_CONTACT', payload);
        });
      } catch (error) {
        logger.addBreadcrumb({
          callingFuncName: 'saveTrustedContact',
          message: `error saving Trusted Contact`,
        });
        logger.logException({error});
      }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async saveBeneficiaries({state, commit, getters: get}: any, accountRequestIds: number[]): Promise<void> {
    const user = await getUser();
    const clUserId = Number(user.userId);

    const {getBeneficiariesByAccountRequestId} = get;

    const promises = [];

    accountRequestIds.forEach((requestId) => {
      const beneficiaries = getBeneficiariesByAccountRequestId(requestId);

      if (beneficiaries.length > 0) {
        promises.push(
          beneficiaryService.saveBeneficiariesForNewAccount({
            clUserId,
            id: requestId,
            beneficiaries,
          })
        );
        commit('SET_HAS_BENEFICIARY', {accountRequestId: requestId, value: true});
      } else {
        let hasBeneficiaries = true;
        const accountIndex = state.hasBeneficiaryCheck.findIndex((account) => account.accountRequestId === requestId);

        if (accountIndex !== -1) {
          hasBeneficiaries = state.hasBeneficiaryCheck[accountIndex].beneficiaries;
        }

        if (hasBeneficiaries) {
          promises.push(beneficiaryService.deleteBeneficiaries(requestId));
          commit('SET_HAS_BENEFICIARY', {accountRequestId: requestId, value: false});
        }
      }
    });
    try {
      await Promise.all(promises);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'saveBeneficiaries',
        message: `error saving beneficiaries`,
      });
      logger.logException({error});
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async removeAddedBeneficiaries({state, commit}: any, accountRequestIds: number[]): Promise<void> {
    const promises = [];

    accountRequestIds.forEach((accountRequestId) => {
      const accountIndex = state.hasBeneficiaryCheck.findIndex((account) => account.accountRequestId === accountRequestId);
      const hasBeneficiaries = state.hasBeneficiaryCheck[accountIndex].beneficiaries;

      if (hasBeneficiaries) {
        promises.push(beneficiaryService.deleteBeneficiaries(accountRequestId));
      }
    });
    await Promise.all(promises);
    accountRequestIds.forEach((accountRequestId) => {
      const payload = {accountRequestId, beneficiaries: []};

      commit('SET_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID', payload);
      commit('SET_HAS_BENEFICIARY', {accountRequestId, value: false});
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async removeAddedTrustedContact({commit, getters: get}: any, accountRequestIds: number[]): Promise<void> {
    const promises = [];

    accountRequestIds.forEach((accountRequestId) => {
      const {trustedContact} = get.trustedContacts.find((account) => account.accountRequestId === accountRequestId);

      if (trustedContact?.id) {
        promises.push(accountRequestService.removeTrustedContact(accountRequestId));
      }
    });
    await Promise.all(promises);
    accountRequestIds.forEach((accountRequestId) => {
      const payload = {
        accountRequestId,
        trustedContact: null,
      };

      commit('SET_UPDATED_TRUSTED_CONTACT', payload);
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async saveTrustedContactChanges({commit, getters: get, rootGetters}: any, accountRequestId: number): Promise<void> {
    const clUserId = rootGetters['cognitoUser/userId'];

    const selectedAccount = get.trustedContacts.find((account) => account.accountRequestId === accountRequestId);

    const payload = {
      clUserId,
      accountRequestId,
      trustedContact: selectedAccount.trustedContact,
      updateCustodian: true,
    };

    await beneficiaryService.saveTrustedContactChanges(payload);
    commit('SET_IS_TRUSTED_CONTACT_UPDATED', false);
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async saveBeneficiaryChanges({commit, getters: get}: any, accountRequestId: number): Promise<void> {
    const user = await getUser();
    const clUserId = Number(user.userId);
    const {getBeneficiariesByAccountRequestId} = get;
    const beneficiaries = getBeneficiariesByAccountRequestId(accountRequestId);

    const payload = {
      clUserId,
      accountRequestId,
      beneficiaries,
      updateCustodian: true,
    };

    await beneficiaryService.saveBeneficiaryChanges(payload);
    commit('SET_IS_BENEFICIARY_UPDATED', false);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resetDesignationsUpdateStatus({commit}: any): void {
    commit('SET_IS_BENEFICIARY_UPDATED', false);
    commit('SET_IS_TRUSTED_CONTACT_UPDATED', false);
  },
};

const mutations = {
  SET_IS_TRUSTED_CONTACT_UPDATED(state: State, payload: boolean): void {
    state.isTrustedContactUpdated = payload;
  },
  SET_IS_BENEFICIARY_UPDATED(state: State, payload: boolean): void {
    state.isBeneficiaryUpdated = payload;
  },
  SET_BENEFICIARIES(state: State, bennies: Beneficiary[]): void {
    state.existingBeneficiaries = bennies;
  },
  SET_ALL_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID(state: State, payload: Record<number, Beneficiary[]>): void {
    state.beneficiariesByAccountRequestId = payload;
  },
  SET_BENEFICIARIES_BY_ACCOUNT_REQUEST_ID(state: State, payload: BeneficiariesByAccountRequestId): void {
    const {accountRequestId, beneficiaries} = payload;

    const beneficiariesByAccountRequestIdCopy = cloneDeep(state.beneficiariesByAccountRequestId);

    beneficiariesByAccountRequestIdCopy[accountRequestId] = beneficiaries;

    state.beneficiariesByAccountRequestId = beneficiariesByAccountRequestIdCopy;
    state.isBeneficiaryUpdated = true;
  },
  SET_TRUSTED_CONTACTS(state: State, activeAccountRequests): void {
    state.trustedContacts = activeAccountRequests.map((request) => ({
      accountRequestId: request.request_id,
      trustedContact: request.trustedContact,
    }));
  },
  SET_SELECTED_OPTION(state: State, activeAccountRequests): void {
    activeAccountRequests.forEach((request) => {
      if (request.trustedContact) {
        state.selectedToAddTrustedContact = true;
      }

      if (request.beneficiaries.length > 0) {
        state.selectedToAddBeneficiary = true;
      }
    });
    state.hasBeneficiaryCheck = activeAccountRequests.map((request) => ({
      accountRequestId: request.request_id,
      beneficiaries: request.beneficiaries.length > 0,
    }));
  },
  SET_HAS_BENEFICIARY(state: State, payload): void {
    const accountIndex = state.hasBeneficiaryCheck.findIndex(
      (account) => account.accountRequestId === payload.accountRequestId
    );

    if (accountIndex === -1) {
      state.hasBeneficiaryCheck.push({
        accountRequestId: payload.accountRequestId,
        beneficiaries: payload.value,
      });
    } else {
      state.hasBeneficiaryCheck[accountIndex].beneficiaries = payload.value;
    }
  },
  SET_SELECTED_ADD_BENEFICIARY_OPTION(state: State, payload): void {
    state.selectedToAddBeneficiary = payload;
  },
  SET_SELECTED_ADD_TRUSTED_CONTACT_OPTION(state: State, payload): void {
    state.selectedToAddTrustedContact = payload;
  },
  SET_UPDATED_TRUSTED_CONTACT(state: State, payload: AccountsTrustedContact): void {
    const accountIndex = state.trustedContacts.findIndex(
      (account) => account.accountRequestId === payload.accountRequestId
    );

    if (accountIndex === -1) {
      state.trustedContacts.push({
        accountRequestId: payload.accountRequestId,
        trustedContact: payload.trustedContact,
      });
    } else {
      state.trustedContacts[accountIndex].trustedContact = payload.trustedContact;
    }

    state.isTrustedContactUpdated = true;
  },
  SET_SELECTED_ACCOUNT_DESIGNATIONS(state: State, accountDetails): void {
    const formattedBeneficiaryObject = {};
    const {id: accountRequestId, beneficiaries, trustedContact} = accountDetails;

    beneficiaries.forEach((beneficiary) => {
      const formattedBeneficiary = {
        ...beneficiary,
        address: {
          ...beneficiary.address,
          country: Constants.DEFAULT_COUNTRY,
        },
      };

      if (Object.prototype.hasOwnProperty.call(formattedBeneficiaryObject, accountRequestId)) {
        formattedBeneficiaryObject[accountRequestId].push(formattedBeneficiary);
      } else {
        formattedBeneficiaryObject[accountRequestId] = [formattedBeneficiary];
      }
    });
    state.beneficiariesByAccountRequestId = formattedBeneficiaryObject;
    state.selectedIndividualAccount = accountDetails.accountType === accountType.individual;
    state.trustedContacts = [
      {
        accountRequestId,
        trustedContact,
      },
    ];
    state.isTrustedContactUpdated = false;
    state.isBeneficiaryUpdated = false;
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
